import { Dispatch, SetStateAction, useMemo } from 'react';

import {
  useRegisterClickerHandlers,
  useRegisterClickerLiveState,
} from '../providers/LiveLessonClickerProvider';
import { Response } from '../services/backendService/types';

// Registers strength-review state and handlers with the central clicker
// provider. Called from every component that hosts the
// StrengthResponseReviewView modal — currently ResponseGridView (regular
// + AI grid) and PodiumView. Cleans up on unmount so a stale "review
// open" doesn't outlive navigation away.
//
// The contract is intentionally narrow: caller supplies the response
// the modal is currently showing, the navigation order, and the
// "advance to next response" handler. Whoever calls this hook is also
// responsible for actually rendering the modal.
export function useStrengthReviewClickerRegistration({
  selectedResponse,
  setSelectedResponse,
  navigationResponses,
  handleSkipForwardResponse,
}: {
  selectedResponse: Response | undefined;
  setSelectedResponse: Dispatch<SetStateAction<Response | undefined>>;
  navigationResponses: Response[] | undefined;
  handleSkipForwardResponse: () => void;
}): void {
  const isStrengthReviewOpen = selectedResponse !== undefined;

  const isStrengthReviewCloseAction = useMemo(() => {
    if (!isStrengthReviewOpen || !navigationResponses || !selectedResponse) {
      return false;
    }
    const responsesWithStrength = navigationResponses.filter(
      r => r.strength?.strengthText || r.strength?.description,
    );
    // Empty `responsesWithStrength` means one of two things — and we
    // INTENTIONALLY treat them the same way:
    //
    //   (a) Generation finished and the model produced zero picks. The
    //       modal has nothing to walk; the next press should close it
    //       and let the teacher move on.
    //   (b) Generation is still in flight. The modal renders a loader.
    //
    // We don't try to differentiate. If the teacher wants to move
    // quickly past a slide where picks aren't ready yet, letting the
    // next clicker press close the loader-modal is the right UX — they
    // shouldn't be trapped waiting on the AI. If they want to wait,
    // they just don't press the clicker again.
    if (responsesWithStrength.length === 0) return true;
    const lastTopPickIndex = navigationResponses.findIndex(
      r => r.id === responsesWithStrength[responsesWithStrength.length - 1].id,
    );
    const currentIndex = navigationResponses.findIndex(r => r.id === selectedResponse.id);
    return currentIndex >= lastTopPickIndex;
  }, [isStrengthReviewOpen, navigationResponses, selectedResponse]);

  useRegisterClickerLiveState(
    {
      isStrengthReviewOpen,
      isStrengthReviewCloseAction,
    },
    ['isStrengthReviewOpen', 'isStrengthReviewCloseAction'],
  );

  useRegisterClickerHandlers(
    {
      nextStrengthResponse: handleSkipForwardResponse,
      closeStrengthReview: () => setSelectedResponse(undefined),
    },
    ['nextStrengthResponse', 'closeStrengthReview'],
  );
}
